import { cloneDeep } from 'lodash';
import { v4 as uuid } from 'uuid';
import { Message } from '@meitu/saas-ui';
import { createCanvas, penBridge } from '@meitu/wasm-lib';
import { getFramework, IFramework, mergeMaterialIds, type IGetSrcImage } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { type MainLayer } from '@/layers/main-layer';
import { renderMainLayerCanvas } from '@/layers/main-layer/render/render-to-canvas';
import { checkNeedMergeCanvas } from '@/layers/main-layer/utils';
import { base64Kit } from '@/utils/base64-kit';
import { isHttpUri, loadImageAsync } from '@/utils/file-kit';
import { uploadBase64ToObs } from '@/utils/upload-kit';
import { IImageLayerProps } from '@/version-types';
import { getFailedText } from './constants';
import { getEliminatePenState, watchEliminatePenConfig } from './states/eliminate-pen-state';
import {
    ELIMINATE_PEN_EVENT,
    EliminateConfig,
    EliminatePenOptions,
    EliminateType,
    IDrawInfo,
    IPathInfo,
    RecognizeTarget,
} from './types';
import {
    createPathInfoWithPointConvert,
    getExtendState,
    getMaskCanvas,
    isAIEliminateUsed,
    updateExtendState,
    updateMaterialIds,
} from './utils';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class EliminatePenPlugin {
    /** 编辑器实例 */
    protected framework: IFramework;

    /** 确认消除完成后的所有功能使用埋点列表 */
    public confirmTrackFuncList: string[] = [];
    /** 当前quickstart面板开启选项对应的Func */
    public recognizeFunc: string = '';

    constructor(identity: string) {
        this.framework = getFramework(identity);
    }

    /** 始终读实时 store，避免缓存快照导致 originLayer 为空 */
    protected getStore() {
        return getEliminatePenState(this.framework.identity);
    }

    protected options: EliminatePenOptions = {};

    setOptions(options: EliminatePenOptions) {
        this.options = options;
    }

    getOptions() {
        return this.options;
    }

    /** 面板 */
    private panel: React.ReactNode | null;

    setPanel(panel: React.ReactNode) {
        this.panel = panel;
    }

    getPanel() {
        return this.panel;
    }

    getScope() {
        // if (this.framework.stage.getStageType() !== StageType.SINGLE) {
        if (!this.framework.service.edgelessCanvas.isSingleMode) {
            return undefined;
        }

        return this.tryGetMainLayer().scope;
    }

    /** 商业化功能 */
    public commercialFunc: string = '';
    getMergeLayerType() {
        return this.tryGetMainLayer().mergeLayerType;
    }

    setCommercialFunc(commercialFunc: string) {
        this.commercialFunc = commercialFunc;
    }

    /** 应用一次消除过程的所有功能使用埋点列表 */
    public applyTrackFuncList: string[] = [];

    /** 用于设置消除笔功能使用的埋点 */
    setTrackFunc(trackFunc: string, isReset = false) {
        if (isReset) {
            this.applyTrackFuncList = trackFunc ? [trackFunc] : [];
            return;
        }

        if (trackFunc) {
            this.applyTrackFuncList = [...new Set([...this.applyTrackFuncList, trackFunc])];
        }
    }

    setRecognizeFunc(func: string) {
        this.recognizeFunc = func;
    }

    getConfig() {
        return this.getStore().eliminateConfig;
    }

    setConfig(config: Partial<EliminateConfig>) {
        this.getStore().setEliminateConfig(config);
    }

    watchConfig(callback: Parameters<typeof watchEliminatePenConfig>['1']) {
        return watchEliminatePenConfig(this.framework.identity, callback);
    }

    /** 打开消除笔 */
    open(layerId?: string) {
        const originIndex = this.framework.service.frame.getActiveFrameId();
        let originLayer = layerId ? this.framework.service.flatLayer.getLayer(layerId) : null;
        // layerId 未命中时回退主图，避免弹窗首开 insert 后 getLayer 时序导致静默失败
        if (!originLayer) {
            originLayer = this.framework.service.frame.getMainLayer(originIndex) ?? null;
        }

        if (!originLayer) return;

        this.getStore().setOriginLayer(originLayer);
    }

    /** 关闭消除笔 */
    close() {
        this.updateLayerPathInfos([]);
        const store = this.getStore();
        store.setOriginLayer(null);
        store.destroy();
        this.confirmTrackFuncList = [];
        this.applyTrackFuncList = [];
    }

    /** 重置消除的图层为原图 */
    reset() {
        const originLayer = this.getStore().originLayer;

        if (!originLayer) {
            return;
        }

        this.getStore().setEliminateConfig({
            autoRecognizeWatermark: false,
            autoRecognizeText: false,
            isWatermarkMaskUsed: false,
            isTextMaskUsed: false,
            watermarkMaskURL: '',
            textMaskURL: '',
            recognizeInfo: null,
        });
        this.framework.service.frame.addOrUpdateLayer(originLayer);
        this.confirmTrackFuncList = [];
        this.applyTrackFuncList = [];
    }

    /** 清空选区 */
    clear() {
        // 清空选区同时要清空自动识别，但需要根据是否已经使用过自动识别来决定是否清空
        this.getStore().setEliminateConfig({
            autoRecognizeWatermark: this.getStore().eliminateConfig.isWatermarkMaskUsed,
            autoRecognizeText: this.getStore().eliminateConfig.isTextMaskUsed,
            autoRecognizeGlasses: this.getStore().eliminateConfig.isGlassesMaskUsed,
            autoRecognizePasserby: this.getStore().eliminateConfig.isPasserbyMaskUsed,
        });

        this.updatePathInfos([]);
    }

    updateLayerPathInfos(newPathInfos: IDrawInfo[], writeHistory = false) {
        const layerId = this.getStore().originLayer?.id;
        const oldLayer = cloneDeep(this.framework.service.flatLayer.getLayer(layerId!));
        const newLayer = cloneDeep(oldLayer);

        if (!layerId || !newLayer) return;

        updateExtendState(newLayer, newPathInfos);
        this.framework.service.frame.addOrUpdateLayer(newLayer);

        if (writeHistory) {
            this.framework.service.history.write(oldLayer, newLayer);
        }
    }

    updatePathInfos(newPathInfos: IDrawInfo[]) {
        this.updateLayerPathInfos(newPathInfos, this.options.isSelectionSaveToHistory);
    }

    /** 自动识别 mask */
    async recognize(target: RecognizeTarget, isOpen: boolean) {
        const { $t } = getLocaleStore();
        const originLayer = this.getStore().originLayer;
        const { watermarkMaskURL, textMaskURL } = this.getStore().eliminateConfig;

        if (!originLayer) return;

        const layer = this.framework.service.flatLayer.getLayer(originLayer.id);

        if (!layer) return;

        // 已经识别过的，不需要再重新识别
        if (target === 'watermark') {
            this.getStore().setEliminateConfig({ autoRecognizeWatermark: isOpen });
            if (watermarkMaskURL) return;
        } else if (target === 'text') {
            this.getStore().setEliminateConfig({ autoRecognizeText: isOpen });
            if (textMaskURL) return;
        }

        // 未开启自动识别，不需要识别
        if (!isOpen) return;

        this.framework.service.mask.show({
            loading: true,
            text: $t((v) => v.mainLayer.operate.autoRecognizing),
        });
        this.framework.service.events.emit(ELIMINATE_PEN_EVENT.LOADING, true);

        try {
            const url = (layer.data as IImageLayerProps).url;

            let originalImageURL = '';
            const originIndex = this.framework.service.frame.getActiveFrameId();
            const layers = this.framework.service.frame.getLayers(originIndex);

            if (checkNeedMergeCanvas(this.getScope(), layers)) {
                const canvasImage = await this.framework.service.result.drawLayers(layers, 'png');
                // 识别走 jpg 效果和速度会更好
                const base64 = canvasImage.toDataURL('image/jpeg', 0.8);
                const result = await uploadBase64ToObs(base64);

                originalImageURL = result.url;
            } else {
                originalImageURL = await base64Kit.getRemoteUrlAsync(url);
            }

            const result = await this.options.recognizeHandler?.(originalImageURL, target);

            if (!result) return;

            const { url: maskURL, recognizeInfo } = result;

            if (target === 'watermark') {
                this.getStore().setEliminateConfig({ watermarkMaskURL: maskURL, recognizeInfo });
            } else if (target === 'text') {
                this.getStore().setEliminateConfig({ textMaskURL: maskURL, recognizeInfo });
            }
        } finally {
            this.framework.service.mask.hide();
            this.framework.service.events.emit(ELIMINATE_PEN_EVENT.LOADING, false);
        }
    }

    private async doEliminate(eliminateMask: HTMLCanvasElement) {
        const { framework } = this;
        const store = this.getStore();

        const originIndex = framework.service.frame.getActiveFrameId();
        const originLayer = store.originLayer;
        const mainLayer = framework.service.flatLayer.getLayer(originLayer?.id || '');
        let layers = framework.service.frame.getLayers(originIndex);

        if (this.getScope() === 'canvas' && this.getMergeLayerType().length > 0) {
            layers = layers.filter((layer) => this.getMergeLayerType().includes(layer.layerType));
        }

        const { type, recognizeInfo } = store.eliminateConfig;

        if (!originLayer || !mainLayer) return;

        const oldLayer = cloneDeep(mainLayer);
        const newLayer = cloneDeep(oldLayer);

        // AI 消除走外部处理
        if (type === EliminateType.AI) {
            const aiEliminateHandler = this.options.aiEliminateHandler;

            if (!aiEliminateHandler) {
                throw new Error('aiEliminateHandler is not set');
            }
            let maskURL = eliminateMask.toDataURL('image/png');

            let originalImageURL = '';

            // ATTENTION: 当前消除笔仅支持主图，所以不用单独判断
            if (checkNeedMergeCanvas(this.getScope(), layers)) {
                const canvasImage = await this.framework.service.result.drawLayers(layers, 'png');
                const base64 = canvasImage.toDataURL('image/png');
                const result = await uploadBase64ToObs(base64);

                originalImageURL = result.url;

                // 合并分离图层 materialIds
                let materialIds: Record<string, string[]> = {};

                layers.forEach((layer) => {
                    // 合并所有图层的 materialIds 到主图
                    materialIds = mergeMaterialIds(materialIds, layer.materialIds);
                });

                newLayer.materialIds = materialIds;
                // 合成画布后， mask 也要基于合成画布重新绘制，这里直接把 mask 图用原来的主图的信息去裁剪旋转等处理
                const maskCanvasImage = renderMainLayerCanvas({ layer: newLayer, baseImage: eliminateMask });

                maskURL = maskCanvasImage.toDataURL('image/png');
            } else {
                originalImageURL = await base64Kit.getRemoteUrlAsync((oldLayer?.data as IImageLayerProps).url);
            }

            let { url: eliminatedImageURL } = await aiEliminateHandler(
                originalImageURL,
                maskURL,
                recognizeInfo,
                this.options.isManualCrop,
            );

            // 当地址是 base64，上传到 base64Kit
            if (!isHttpUri(eliminatedImageURL)) {
                eliminatedImageURL = base64Kit.getCode(eliminatedImageURL, uuid());
            }

            // 先加载图片，避免 loading 先关闭才会显示结果图
            await loadImageAsync(eliminatedImageURL);
            // 更新图层数据
            (newLayer.data as IImageLayerProps).url = eliminatedImageURL;
            newLayer.isAILayer = true;
        } else {
            // ATTENTION: 手动消除当前还不支持 scope 为 canvas 的情况
            const object = framework.service.edgelessCanvas.getByLayerId(originLayer.id) as unknown as
                | IGetSrcImage
                | undefined;

            if (!object?.getSrcImage) {
                throw new Error('Eliminate source image is not ready');
            }

            const sourceImage = await object.getSrcImage();
            const fromCanvas = createCanvas(sourceImage.naturalWidth, sourceImage.naturalHeight, sourceImage);
            // 本地消除走 ImageKit 处理
            const res = await penBridge.from(fromCanvas).eliminate(eliminateMask);

            if (!res.canvas) return;

            // 更新图层数据
            const base64 = res.canvas.toDataURL();

            (newLayer.data as IImageLayerProps).url = base64Kit.getCode(base64, uuid());
        }

        updateMaterialIds(newLayer, type, this.commercialFunc);
        // 消除完成后，需要清空已经绘制的选区
        updateExtendState(newLayer, []);

        const trackFunc = this.applyTrackFuncList.join(',');

        if (checkNeedMergeCanvas(this.getScope(), layers)) {
            // 画布模式下，需要替换除背景的所有的图层
            const layers = framework.service.frame
                .getLayers(originIndex)
                .filter((layer) => this.getMergeLayerType().includes(layer.layerType));
            const replaceIds = layers.filter((layer) => layer.layerType !== 'bg').map((layer) => layer.id);

            const imageLayerData = newLayer.data as IImageLayerProps;
            // ATTENTION: 合成之后需要把主图改成非透明
            imageLayerData.isTransparent = false;
            imageLayerData.imageTransform = {
                offsetX: 0,
                offsetY: 0,
                rotate: 0,
                scale: 1,
                flipH: false,
                flipV: false,
                widthRatio: 1,
                heightRatio: 1,
            };
            imageLayerData.naturalHeight = newLayer.height;
            imageLayerData.naturalWidth = newLayer.width;
            newLayer.rotate = 0;
            newLayer.left = 0;
            newLayer.top = 0;
            this.framework.service.stage.replaceLayer(replaceIds, newLayer, undefined, trackFunc);
        } else {
            framework.service.frame.addOrUpdateLayer(newLayer);
            framework.service.history.write(oldLayer, newLayer, trackFunc);
        }
    }

    async eliminate(pathInfos: IPathInfo[]) {
        const { $t } = getLocaleStore();
        const { framework } = this;
        const store = this.getStore();
        const eliminateConfig = store.eliminateConfig;
        const { type, autoRecognizeWatermark, autoRecognizeText, isWatermarkMaskUsed, isTextMaskUsed } =
            eliminateConfig;

        const eliminateMaskCanvas = await getMaskCanvas(pathInfos, store.originLayer, eliminateConfig);

        if (!eliminateMaskCanvas) return;

        framework.service.mask.show({
            loading: true,
            text: $t((v) => v.mainLayer.operate.handleMessage),
        });
        framework.service.events.emit(ELIMINATE_PEN_EVENT.LOADING, true);

        // 让loading先弹出，在执行消除
        await delay(30);

        try {
            await this.doEliminate(eliminateMaskCanvas);

            if (type === EliminateType.AI) {
                if (autoRecognizeWatermark && !isWatermarkMaskUsed) {
                    this.getStore().setEliminateConfig({ isWatermarkMaskUsed: true });
                }

                if (autoRecognizeText && !isTextMaskUsed) {
                    this.getStore().setEliminateConfig({ isTextMaskUsed: true });
                }
            }
        } catch (e: any) {
            console.error(e);
            if (e.data?.code === 10025) {
                // 入参审核不通过
                Message.warn($t((v) => v.mainLayer.operate.inputAuditFailedTips));
                return;
            }
            const errorType = (e as unknown as null | { name: string })?.name;

            // HACK: 当前 core 不想依赖 ai api sdk ，所以这里直接写死错误值
            if (errorType === 'EraserNoMaskError') {
                Message.warn($t((v) => v.mainLayer.operate.emptyAreaTips));
            } else if (errorType === 'AuditError') {
                // 消除结果审核不通过
                Message.warn($t((v) => v.mainLayer.operate.auditFailedTips));
            } else {
                Message.error($t((v) => v.mainLayer.operate.errorMessage));
            }

            throw new Error(`eliminate error: ${errorType}`);
        } finally {
            framework.service.mask.hide();
            framework.service.events.emit(ELIMINATE_PEN_EVENT.APPLY_END);
            framework.service.events.emit(ELIMINATE_PEN_EVENT.LOADING, false);
        }
    }

    // TODO: @cxz1 需要抽离公共的图层处理代码
    private async doQuickEliminate(target: RecognizeTarget) {
        const { framework } = this;
        const store = this.getStore();
        const originIndex = framework.service.frame.getActiveFrameId();
        const originLayer = store.originLayer;
        const mainLayer = framework.service.flatLayer.getLayer(originLayer?.id || '');
        const { type } = store.eliminateConfig;

        if (!originLayer || !mainLayer) return;

        const oldLayer = cloneDeep(mainLayer);
        const newLayer = cloneDeep(oldLayer);

        const originalImageURL = (oldLayer?.data as IImageLayerProps).url;
        let layers = this.framework.service.frame.getLayers(originIndex);

        if (this.getScope() === 'canvas' && this.getMergeLayerType().length > 0) {
            layers = layers.filter((layer) => this.getMergeLayerType().includes(layer.layerType));
        }

        let oldURL = '';

        if (checkNeedMergeCanvas(this.getScope(), layers)) {
            const canvasImage = await this.framework.service.result.drawLayers(layers, 'png');

            // TODO: @cxz1 检查当前是否是透明度
            const base64 = canvasImage.toDataURL('image/png');
            const result = await uploadBase64ToObs(base64);

            oldURL = result.url;
        } else {
            oldURL = await base64Kit.getRemoteUrlAsync(originalImageURL);
        }

        const recognizeAndEliminateHandler = this.options.recognizeAndEliminateHandler;

        if (!recognizeAndEliminateHandler) {
            throw new Error('recognizeAndEliminateHandler is not set');
        }
        const res = await recognizeAndEliminateHandler(oldURL, target).catch((e) => {
            this.setAutoRecognize(target, false);
            this.recognizeFunc = '';
            throw e;
        });
        const newURL = res.url;

        if (this.recognizeFunc) {
            this.applyTrackFuncList.push(this.recognizeFunc);
        }

        // 先加载图片，避免 loading 先关闭才会显示结果图
        await loadImageAsync(newURL);
        // 更新图层数据
        (newLayer.data as IImageLayerProps).url = newURL;
        // 更新图层以自动识别并消除的类型
        newLayer.extraInfo = {
            ...newLayer.extraInfo,
            eliminateTarget: {
                ...newLayer.extraInfo?.eliminateTarget,
                [target]: true,
            },
        };
        newLayer.isAILayer = true;
        updateMaterialIds(newLayer, type, this.commercialFunc);
        // 消除完成后，需要清空已经绘制的选区
        updateExtendState(newLayer, []);

        const trackFunc = this.applyTrackFuncList.join(',');

        if (checkNeedMergeCanvas(this.getScope(), layers)) {
            // 画布模式下，需要替换除背景的所有的图层
            const layers = framework.service.frame
                .getLayers(originIndex)
                .filter((layer) => this.getMergeLayerType().includes(layer.layerType));
            const replaceIds = layers.filter((layer) => layer.layerType !== 'bg').map((layer) => layer.id);

            // ATTENTION: 合成之后需要把主图改成非透明
            (newLayer.data as IImageLayerProps).isTransparent = false;
            this.framework.service.stage.replaceLayer(replaceIds, newLayer, undefined, trackFunc);
        } else {
            framework.service.frame.addOrUpdateLayer(newLayer);
            framework.service.history.write(oldLayer, newLayer, trackFunc);
        }
    }

    private setAutoRecognize(target: RecognizeTarget, isOpen: boolean) {
        if (target === 'text') {
            this.getStore().setEliminateConfig({ autoRecognizeText: isOpen });
        } else if (target === 'watermark') {
            this.getStore().setEliminateConfig({ autoRecognizeWatermark: isOpen });
        } else if (target === 'passerby') {
            this.getStore().setEliminateConfig({ autoRecognizePasserby: isOpen });
        } else if (target === 'glasses') {
            this.getStore().setEliminateConfig({ autoRecognizeGlasses: isOpen });
        } else if (target === 'glare') {
            this.getStore().setEliminateConfig({ autoGlare: isOpen });
        } else if (target === 'logo') {
            this.getStore().setEliminateConfig({ autoRecognizeLogo: isOpen });
        }
    }

    private async quickEliminate(target: RecognizeTarget) {
        const { $t } = getLocaleStore();
        const { framework } = this;

        this.setAutoRecognize(target, true);

        framework.service.mask.show({
            loading: true,
            text: $t((v) => v.mainLayer.operate.handleMessage),
        });
        framework.service.events.emit(ELIMINATE_PEN_EVENT.LOADING, true);

        // 让loading先弹出，在执行消除
        await delay(30);

        try {
            await this.doQuickEliminate(target);
        } catch (e: unknown) {
            console.error(e);
            const errorType = (e as unknown as null | { name: string })?.name;

            // HACK: 当前 core 不想依赖 ai api sdk ，所以这里直接写死错误值
            if (errorType === 'EraserNoMaskError') {
                const failedText = getFailedText(target);
                Message.warn(failedText);
            } else {
                Message.error($t((v) => v.mainLayer.operate.errorMessage));
            }

            throw new Error(`eliminate error: ${errorType}`);
        } finally {
            framework.service.mask.hide();
            framework.service.events.emit(ELIMINATE_PEN_EVENT.APPLY_END);
            framework.service.events.emit(ELIMINATE_PEN_EVENT.LOADING, false);
        }
    }

    private tryGetMainLayer() {
        const mainLayer = this.framework.service.registration.getInstance<MainLayer>('MainLayer');

        if (!mainLayer) {
            throw new Error('MainLayer is not found');
        }

        return mainLayer;
    }

    /** 执行消除 */
    async applyEliminate(target?: RecognizeTarget) {
        const layerId = this.getStore().originLayer?.id;

        if (!layerId) return;

        const layer = this.framework.service.flatLayer.getLayer(layerId);

        if (!layer) return;

        if (target) {
            await this.quickEliminate(target);
            return;
        }

        const drawInfos = getExtendState(layer);
        const pathInfos = createPathInfoWithPointConvert(layer, drawInfos, true);

        await this.eliminate(pathInfos);
    }

    /** 获取消除编辑的最终数据 */
    getResult() {
        const layerId = this.getStore().originLayer?.id;

        if (!layerId) return null;

        const trackFunc = this.confirmTrackFuncList?.join(',');
        const layer = this.framework.service.flatLayer.getLayer(layerId);
        const oldURL = (this.getStore().originLayer?.data as IImageLayerProps).url;
        const newURL = (layer.data as IImageLayerProps).url;
        console.log('===>getConfirmResult', { oldURL, newURL });

        const isAiEliminateUsed = isAIEliminateUsed(layer, this.commercialFunc);

        return {
            url: newURL,
            trackFunc,
            isAiEliminateUsed,
        };
    }

    // 对外暴露的事件
    /**
     * 画笔绘制结束后的回调函数，通常用于埋点上报
     * @param callback -
     * @returns
     */
    onDrawEnd(callback: () => void) {
        const handler = this.framework.service.events.on(ELIMINATE_PEN_EVENT.DRAW_END, callback);

        return () => {
            this.framework.service.events.off(handler);
        };
    }

    /**
     * 应用消除效果后的回调函数，通常用于上报埋点
     * @param callback
     * @returns
     */
    onApplyEnd(callback: (trackList: string[]) => void) {
        const handler = this.framework.service.events.on(ELIMINATE_PEN_EVENT.APPLY_END, () => {
            callback(this.applyTrackFuncList);
            // 把应用消除的埋点数据，统一写入到确认消除埋点列表
            this.confirmTrackFuncList = [...new Set([...this.confirmTrackFuncList, ...this.applyTrackFuncList])];
            // 应用结束后，重置埋点
            this.setTrackFunc('', true);
        });

        return () => {
            this.framework.service.events.off(handler);
        };
    }

    /**
     * 用于监听消除笔 loading 状态
     * @param callback -
     * @returns
     */
    onLoading(callback: (value: boolean) => void) {
        const handler = this.framework.service.events.on(ELIMINATE_PEN_EVENT.LOADING, callback);

        return () => {
            this.framework.service.events.off(handler);
        };
    }
}
